import React, {useState, useEffect} from 'react';
import {pick} from 'lodash';
import {
    Drawer,
    FormGroup,
    InputGroup,
    Button,
    Callout,
    Radio,
    RadioGroup,
    HTMLSelect,
    Spinner,
    Alert,
} from '@blueprintjs/core';
import styled from 'styled-components';
import getRoleMappings from '../../../../utils/userRoleMappings';
import * as AuthAPI from '../../../../services/auth-api';
import {successToast, errorToast} from '../../../../utils/toaster';


const FormWrapper = styled.div`
    width: 90%;
    margin: 2em auto;
`;

const Section = styled.div`
    margin-bottom: 2em;
`;

const Table = styled.table`
    width: 100%;
    margin: 1em 0;
`;

const SmallLabel = styled.p`
    color: #555;
    font-size: 12px;
    margin: 0;
    ${props => (props.$marginBottom ? 'margin-bottom: 1em' : '')}
`;


const UserDrawer = ({user, orgId, orgName, isOpen, onClose, onUserUpdated, onUserDeleted}) => {
    // edit user state
    const [fullName, setFullName] = useState('');
    const [status, setStatus] = useState('');
    const [editLoading, setEditLoading] = useState(false);

    // roles state
    const [currentRoles, setCurrentRoles] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [roleToAdd, setRoleToAdd] = useState('');
    const [rolesLoading, setRolesLoading] = useState(false);
    const [roleResponse, setRoleResponse] = useState({});

    // delete state
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const roleMappings = getRoleMappings(orgName);

    // initialise form when drawer opens
    useEffect(() => {
        if (isOpen && user) {
            setFullName(user.fullName || '');
            setStatus(user.status || '');
            setRoleResponse({});
        }
    }, [isOpen, user]);

    // fetch roles when drawer opens
    useEffect(() => {
        if (!isOpen || !user) return;

        async function fetchRoles() {
            setRolesLoading(true);
            try {
                const {data: allRoles} = await AuthAPI.roles.list();
                const {data: userRoles} = await AuthAPI.users.rolesForUser(user.id, orgId);

                const available = allRoles.filter(r => {
                    const mapping = roleMappings[r.name] || {};
                    if (mapping.createDisabled) return false;
                    return !userRoles.find(cr => cr.id === r.id);
                });

                setCurrentRoles(userRoles);
                setAvailableRoles(available);
                setRoleToAdd(available.length > 0 ? available[0].id : '');
            } catch (e) {
                console.error(e);
            } finally {
                setRolesLoading(false);
            }
        }

        fetchRoles();
    }, [isOpen, user]);

    const editUser = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        try {
            await AuthAPI.users.update(user.id, {
                ...pick(user, 'primaryOrgId'),
                email: user.email,
                fullName,
                status,
            });
            successToast({message: 'User updated'});
            onUserUpdated();
        } catch (e) {
            errorToast({message: 'Failed to update user'});
        } finally {
            setEditLoading(false);
        }
    };

    const removeRole = async (role) => {
        setRoleResponse({type: 'info', text: 'Removing...'});
        try {
            await AuthAPI.users.removeRole(user.id, orgId, role.id);
            setCurrentRoles(prev => prev.filter(r => r.id !== role.id));
            setAvailableRoles(prev => {
                const updated = [...prev, role];
                setRoleToAdd(updated[0]?.id || '');
                return updated;
            });
            setRoleResponse({type: 'success', text: 'Role removed'});
        } catch (e) {
            setRoleResponse({type: 'danger', text: 'Could not remove role'});
        }
    };

    const addRole = async () => {
        if (!roleToAdd) return;
        setRoleResponse({type: 'info', text: 'Adding...'});
        try {
            await AuthAPI.users.addRole(user.id, orgId, roleToAdd);
            const added = availableRoles.find(r => Number(r.id) === Number(roleToAdd));
            setAvailableRoles(prev => {
                const updated = prev.filter(r => Number(r.id) !== Number(roleToAdd));
                setRoleToAdd(updated[0]?.id || '');
                return updated;
            });
            setCurrentRoles(prev => [...prev, added]);
            setRoleResponse({type: 'success', text: 'Role added'});
        } catch (e) {
            setRoleResponse({type: 'danger', text: 'Could not add role'});
        }
    };

    const deleteUser = async () => {
        setDeleteLoading(true);
        try {
            await AuthAPI.users.delete(user.id);
            successToast({message: `${user.email} deleted`});
            setDeleteAlertOpen(false);
            onUserDeleted();
        } catch (e) {
            errorToast({message: 'Could not delete user'});
        } finally {
            setDeleteLoading(false);
        }
    };

    if (!user) return null;

    return (
        <>
            <Drawer
                icon="person"
                title={user.email}
                isOpen={isOpen}
                onClose={onClose}
                size="40%"
            >
                <FormWrapper>
                    <Section>
                        <h3>Edit User</h3>
                        <form onSubmit={editUser}>
                            <FormGroup label="Email">
                                <InputGroup value={user.email} disabled />
                            </FormGroup>
                            <FormGroup label="Full name">
                                <InputGroup
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                />
                            </FormGroup>
                            <RadioGroup
                                label="Status"
                                onChange={(e) => setStatus(e.target.value)}
                                selectedValue={status}
                            >
                                <Radio label="Active" value="active" />
                                <Radio label="Created (prompted for name on dashboard)" value="created" />
                                <Radio label="Inactive (can log in but no data access)" value="inactive" />
                            </RadioGroup>
                            <Button type="submit" loading={editLoading} intent="primary" text="Update user" />
                        </form>
                    </Section>

                    <Section>
                        <h3>Roles in {orgName}</h3>
                        {rolesLoading ? <Spinner size={30} /> : (
                            <>
                                <Table className="bp6-html-table bp6-html-table-bordered bp6-interactive">
                                    <thead>
                                        <tr>
                                            <th>Role</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRoles.map(role => {
                                            if (!role) return null;
                                            const mapping = roleMappings[role.name] || {};
                                            return (
                                                <tr key={role.id}>
                                                    <td>
                                                        {mapping.label || role.name}
                                                        <SmallLabel>{mapping.description}</SmallLabel>
                                                    </td>
                                                    <td>
                                                        <Button
                                                            small
                                                            intent="warning"
                                                            text="Remove"
                                                            disabled={mapping.deleteDisabled !== false}
                                                            onClick={() => removeRole(role)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                                <Callout title="Assign a new role">
                                    <SmallLabel $marginBottom>
                                        Only shows roles not marked as "Internal use only".
                                    </SmallLabel>
                                    <FormGroup>
                                        <HTMLSelect
                                            onChange={(e) => setRoleToAdd(e.target.value)}
                                            value={roleToAdd}
                                            options={
                                                availableRoles.length === 0
                                                    ? [{label: 'No other roles to add...', value: ''}]
                                                    : availableRoles.map(role => {
                                                        const mapping = roleMappings[role.name] || {};
                                                        const desc = mapping.description ? ` (${mapping.description})` : '';
                                                        return {
                                                            label: (mapping.label || role.name) + desc,
                                                            value: role.id,
                                                        };
                                                    })
                                            }
                                        />
                                        <Button text="Add" intent="primary" onClick={addRole} />
                                    </FormGroup>
                                </Callout>
                                {roleResponse.type && (
                                    <Callout intent={roleResponse.type}>{roleResponse.text}</Callout>
                                )}
                            </>
                        )}
                    </Section>

                    <Section>
                        <Callout intent="danger" title="Danger zone">
                            <Button
                                intent="danger"
                                icon="trash"
                                text="Delete user"
                                onClick={() => setDeleteAlertOpen(true)}
                            />
                        </Callout>
                    </Section>
                </FormWrapper>
            </Drawer>
            <Alert
                isOpen={deleteAlertOpen}
                cancelButtonText="Cancel"
                confirmButtonText="Delete user"
                intent="danger"
                icon={deleteLoading ? <Spinner size={40} /> : "trash"}
                canEscapeKeyCancel
                canOutsideClickCancel
                onCancel={() => setDeleteAlertOpen(false)}
                onConfirm={deleteUser}
            >
                Are you sure you want to delete {user.email}?
            </Alert>
        </>
    );
};

export {UserDrawer};
